import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Search, MessageCircle, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useChatPopup } from './ChatPopupManager';
import { Badge } from '@/components/ui/badge';
import { createChatRoomName, getUserEntityId } from '@/lib/chat-utils';
import { messagingApi } from '@/api/messaging';
import { useSearchUsers, SearchUserResult } from '@/hooks/use-search-filmmakers';
import { useDebouncedValue } from '@/hooks/use-debounced-value';

interface NewChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** 'both' = dual user; search across BOTH filmmakers and clients */
  userRole: 'client' | 'filmmaker' | 'both' | 'admin';
}

export const NewChatModal: React.FC<NewChatModalProps> = ({ isOpen, onClose, userRole }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedQuery = useDebouncedValue(searchQuery, 300);
  const { openChat, toggleChatList } = useChatPopup();
  const isDual = userRole === 'both';

  // Determine account type filter based on user role.
  // Dual users and admins see EVERYONE so they can message both filmmakers and clients.
  const accountTypeFilter = useMemo(() => {
    if (isDual) return undefined;
    if (userRole === 'client') return 'has_filmmaker' as const;
    if (userRole === 'filmmaker') return 'has_client' as const;
    return 'any' as const; // Admin can message anyone
  }, [userRole, isDual]);

  // Use the unified search API
  const { data: searchResults, isLoading } = useSearchUsers(
    {
      query: debouncedQuery.trim() || undefined,
      account_type: accountTypeFilter,
      limit: 30,
    },
    { enabled: isOpen }
  );

  const handleStartChat = async (user: SearchUserResult) => {
    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) {
        toast.error('Please log in to send messages');
        return;
      }

      // For dual users, pick our side based on the recipient's role:
      //   recipient is filmmaker → we act as client (use our client_id)
      //   recipient is client    → we act as filmmaker (use our filmmaker_id)
      // For single-role users, our entity is determined by our role.
      let currentEntityId: string | null = null;
      let ourActingRole: 'client' | 'filmmaker' | 'admin' = userRole as any;

      if (isDual) {
        ourActingRole = (user.has_filmmaker_account && user.filmmaker_id) ? 'client' : 'filmmaker';
        currentEntityId = await getUserEntityId(currentUser.id, ourActingRole as 'client' | 'filmmaker');
      } else {
        currentEntityId = await getUserEntityId(currentUser.id, userRole);
      }

      if (!currentEntityId) {
        toast.error('Unable to start chat. Please try again.');
        return;
      }

      // Get the target entity ID based on their account type
      const targetEntityId = ourActingRole === 'client'
        ? user.filmmaker_id
        : user.client_id;

      if (!targetEntityId) {
        toast.error('Unable to find user. Please try again.');
        return;
      }

      // Create proper room name
      let roomName: string;
      if (ourActingRole === 'client') {
        roomName = createChatRoomName(currentEntityId, targetEntityId);
      } else if (ourActingRole === 'filmmaker') {
        roomName = createChatRoomName(targetEntityId, currentEntityId);
      } else {
        // Admin
        if (user.has_client_account && user.client_id) {
          roomName = createChatRoomName(user.client_id, currentEntityId);
        } else if (user.filmmaker_id) {
          roomName = createChatRoomName(currentEntityId, user.filmmaker_id);
        } else {
          toast.error('Unable to start chat with this user.');
          return;
        }
      }

      // Use display_name from API (already respects privacy settings)
      const displayName = user.display_name || user.company_name || 'User';

      openChat({
        id: roomName,
        name: displayName,
        avatar: user.profile_pic || undefined,
        role: user.has_filmmaker_account ? 'filmmaker' : 'client',
        isMinimized: false,
        unreadCount: 0
      });

      // Chat room will be created lazily when the first message is sent
      // This prevents the recipient from seeing an empty chat before any real message

      window.dispatchEvent(new CustomEvent('new-chat-created'));
      onClose();
      toggleChatList();
      toast.success(`Started chat with ${displayName}`);
    } catch (error) {
      console.error('Error starting chat:', error);
      toast.error('Failed to start chat');
    }
  };

  const targetRoleLabel = isDual
    ? 'users'
    : userRole === 'client'
      ? 'filmmakers'
      : userRole === 'filmmaker'
        ? 'clients'
        : 'users';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-h-[80vh] p-0 new-chat-modal">
        <DialogHeader className="p-4 pb-0">
          <DialogTitle>Start New Conversation</DialogTitle>
        </DialogHeader>

        <div className="p-4 pb-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={`Search ${targetRoleLabel}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>

        <ScrollArea className="h-[400px] px-4 pb-4">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin mb-2" />
              <span>Searching...</span>
            </div>
          ) : searchResults && searchResults.length > 0 ? (
            <div className="space-y-2">
              {!debouncedQuery && (
                <div className="text-xs text-muted-foreground mb-2">
                  Verified {targetRoleLabel} - Type to search
                </div>
              )}
              {searchResults.map((user) => {
                // Use display_name from API (already respects privacy settings)
                const displayName = user.display_name || user.company_name || 'User';
                const roleLabel = user.has_filmmaker_account ? 'filmmaker' : 'client';

                return (
                  <div
                    key={user.user_id}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-accent cursor-pointer transition-colors"
                    onClick={() => handleStartChat(user)}
                  >
                    <Avatar className="h-10 w-10">
                      {user.profile_pic ? (
                        <AvatarImage src={user.profile_pic} />
                      ) : (
                        <AvatarFallback>
                          {displayName.slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      )}
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium truncate">{displayName}</p>
                        <Badge variant="outline" className="text-xs">
                          {roleLabel}
                        </Badge>
                      </div>
                    </div>
                    <MessageCircle className="h-4 w-4 text-muted-foreground" />
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              {debouncedQuery ? (
                <div className="space-y-2">
                  <p>No {targetRoleLabel} found matching "{debouncedQuery}"</p>
                  <p className="text-xs">Try a different search term</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <p>No {targetRoleLabel} available</p>
                  <p className="text-xs">Try searching by name</p>
                </div>
              )}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
